'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { ChevronDown, ChevronUp } from 'lucide-react';
import LandingNav from '@/app/public-landing-page/components/LandingNav';
import LandingFooter from '@/app/public-landing-page/components/LandingFooter';

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="public-card cursor-pointer transition-[background-color,border-color]"
      style={{
        borderColor: open ? 'var(--public-emerald)' : 'var(--public-border)',
        background: open ? 'var(--public-bg-subtle)' : 'var(--public-surface)',
      }}
      onClick={() => setOpen(!open)}
    >
      <div className="flex items-center justify-between p-4 sm:p-5">
        <p className="pr-4 text-sm font-semibold text-[var(--public-text)]">{q}</p>
        {open ? (
          <ChevronUp size={16} className="flex-shrink-0 text-[var(--public-emerald)]" />
        ) : (
          <ChevronDown size={16} className="text-slate-400 flex-shrink-0" />
        )}
      </div>
      {open && (
        <p className="px-4 pb-4 text-sm leading-relaxed text-[var(--public-text-muted)] sm:px-5 sm:pb-5">
          {a}
        </p>
      )}
    </div>
  );
}

const platformFaqs = [
  {
    q: 'What exactly is Ertzyx?',
    a: 'Ertzyx is a dual-layer platform. For individuals, it is a permanent social storytelling platform where you share and preserve life achievements across 7 pillars. For institutions, it is a verification infrastructure that authenticates those achievements with a trusted digital signature. Together they create a trusted identity ecosystem across personal, corporate, governmental, and social systems.',
  },
  {
    q: 'What is the difference between a Story and an Achievement?',
    a: 'A Story is any life moment you want to preserve or share: a memory, a struggle, or a win. No evidence is needed. A Story lives in Story Hub and can be shared in Community Explorer. An Achievement is a formal credential: a degree, a certification, or an award, that you submit to a partner institution for official verification. Achievements live in your vault and come with a permanent public credential URL once verified.',
  },
  {
    q: 'What is Community Explorer?',
    a: 'Community Explorer is the public feed where stories from across the Ertzyx network appear. When you publish a story publicly, it appears here for other users to discover, witness, and react to. Think of it as the social timeline of Ertzyx: permanent and organized by life pillars.',
  },
  {
    q: 'What is Story Hub?',
    a: 'Story Hub is your private workspace for writing and managing your stories. You can write in multiple formats (written, voice, photo, video, podcast, document, or link), set visibility, and organize your stories before publishing. Stories in Story Hub are only visible to you until you publish them.',
  },
  {
    q: 'What does "Witnessed" mean?',
    a: 'Witnessing a story means you have read it and believe it to be true. It is a form of community validation, like a character reference from your network. Anyone on Ertzyx can witness any public story. Institutional verification (Gold badge) is different. That requires a partner institution to authenticate the record with official evidence.',
  },
  {
    q: 'What is my Public Timeline?',
    a: 'Your Public Timeline is the section of your profile that shows your public stories to anyone who visits your vault link. It is separate from Community Explorer. Your Public Timeline shows only your stories, while Community Explorer shows stories from everyone on the platform.',
  },
  {
    q: 'Who can verify my achievements?',
    a: 'Partner institutions registered on Ertzyx can verify achievements. These include universities, schools, cultural organizations, professional associations, and government bodies. You submit your achievement with evidence, the institution reviews it, and if approved they issue a Gold verification confirmation with a public verification link.',
  },
  {
    q: 'Can I use Ertzyx on mobile?',
    a: 'Yes. Ertzyx works in any mobile browser on iOS and Android. Open ledger.ertzyx.com in Safari or Chrome on your phone. A dedicated app is in development.',
  },
  {
    q: 'How does institutional verification work?',
    a: 'You submit an achievement and select a registered institution. The institution receives it in their pending queue, reviews it, and approves with their digital signature. The verified record is permanently stored on the Ertzyx ledger, readable by anyone with your vault link.',
  },
];

const billingFaqs = [
  {
    q: 'Is posting stories really free and unlimited?',
    a: 'Yes, completely free and completely unlimited. Like any social platform, you can post, follow, like, comment, and witness stories at no cost. What you pay for on Ertzyx is institutional verification, which turns your stories into institution-confirmed, status-verifiable records.',
  },
  {
    q: 'What is the difference between Free and Verified?',
    a: 'Free gives you unlimited storytelling and up to 3 verified achievement records with 100 MB media storage. Verified unlocks unlimited achievement records, the Story Hub with voice, photos, podcasts, and documents (video on Legacy), 5 GB media vault storage, analytics, and the ERTZYX Verified badge that shows your credentials are institutionally authenticated.',
  },
  {
    q: 'Can I upgrade or downgrade anytime?',
    a: 'Yes, changes take effect immediately. You can upgrade or downgrade at any time from your Account and Billing page. No penalties or lock-in periods.',
  },
  {
    q: 'What happens to my records if I cancel?',
    a: 'Your records are preserved forever on the Ertzyx ledger. If you cancel, you keep your vault, ERV ID, all story posts, and all achievement records permanently. You simply lose access to paid features like analytics and the Verified badge.',
  },
  {
    q: 'Do institutions pay per verification?',
    a: 'No. Institutions pay a flat monthly subscription. The Starter plan covers up to 500 verifications per month. The Growth plan is unlimited.',
  },
  {
    q: 'What is Verification-as-a-Service (VaaS)?',
    a: 'VaaS is a one-time $29 payment to get a single achievement professionally verified without a subscription.',
  },
];

export default function FaqPage() {
  return (
    <main className="public-page public-family-editorial">
      <LandingNav />
      <div className="mx-auto max-w-3xl px-4 pb-20 pt-28">
        {/* Back link */}
        <Link
          href="/"
          className="mb-8 inline-flex items-center gap-1 text-sm font-semibold text-[var(--public-emerald)] transition-colors hover:text-[var(--public-forest)]"
        >
          Back to home
        </Link>

        {/* Header */}
        <div className="mt-8 mb-12">
          <h1
            className="public-heading mb-3"
            style={{
              fontFamily: '"DM Serif Display", serif',
              fontSize: 38,
              lineHeight: 1.15,
            }}
          >
            Frequently Asked Questions
          </h1>
          <p className="text-base text-[var(--public-text-muted)]">
            Everything you need to know about Ertzyx
          </p>
        </div>

        {/* Platform section */}
        <div className="mb-10">
          <p
            className="text-xs font-semibold uppercase tracking-widest mb-4"
            style={{ color: '#4a5d72', letterSpacing: '0.12em' }}
          >
            Platform
          </p>
          <div className="flex flex-col gap-3">
            {platformFaqs.map((item) => (
              <FaqItem key={item.q} q={item.q} a={item.a} />
            ))}
          </div>
        </div>

        {/* Plans and Billing section */}
        <div className="mb-16">
          <p
            className="text-xs font-semibold uppercase tracking-widest mb-4"
            style={{ color: '#4a5d72', letterSpacing: '0.12em' }}
          >
            Plans and Billing
          </p>
          <div className="flex flex-col gap-3">
            {billingFaqs.map((item) => (
              <FaqItem key={item.q} q={item.q} a={item.a} />
            ))}
          </div>
        </div>

        {/* Footer CTA */}
        <div
          style={{
            borderRadius: 14,
            border: '1px solid var(--public-border)',
            background: 'var(--public-surface)',
            padding: '32px 28px',
            textAlign: 'center',
          }}
        >
          <p
            className="public-heading mb-2"
            style={{ fontFamily: '"DM Serif Display", serif', fontSize: 20 }}
          >
            Still have questions?
          </p>
          <p className="mb-5 text-sm text-[var(--public-text-muted)]">Our team is happy to help.</p>
          <Link href="/contact" className="public-button public-button-primary">
            Contact us
          </Link>
        </div>
      </div>
      <LandingFooter />
    </main>
  );
}
